import { WideType } from './wide-type';
import { AnimatedAnswerType } from './animated-answer-type';
import { AnimatedHeadingType } from './animated-heading-type';
import {
  FC,
  useState,
  useRef,
  useEffect,
  useMemo,
  ReactElement,
  Children,
  cloneElement,
  ReactNode,
} from 'react';
import { useMediaQuery } from '@/utils/use-media-query';
import { AnnotatedType } from '@/components/annotated-type';
import { styled } from '@/styles/stitches.config';

// takes a string and splits it up in either <span>s by word or wraps it into <WideType/>
const parseString = (
  string: string,
  refs: [HTMLSpanElement | null, boolean][],
  offset = 0,
  wide = false,
  type: 'answer' | 'heading' | 'body' | null = null
) => {
  if (!wide) {
    const words = string.split(' ');
    return words.map((word, i) => {
      return (
        <span
          key={offset + i}
          ref={(el) => {
            refs[offset + i] = [el, wide];
          }}
        >
          {word}
          {/*  Add white-space when this is not the last word */}
          {i !== words.length - 1 && ' '}
        </span>
      );
    });
  } else {
    return [
      <WideType
        css={{
          '--roboto-flex-xtra': type !== 'body' ? 600 : 468,
          fontStretch: type === 'heading' ? '150%' : '100%',
          boxDecorationBreak: 'clone',
          '-webkit-box-decoration-break': 'clone',
        }}
        key={string}
        ref={(ref) => {
          if (ref) {
            const { el, i } = ref;
            refs[offset + i] = [el, wide];
          }
        }}
      >
        {string}
      </WideType>,
    ];
  }
};

interface TextProps {
  children: string | ReactElement<HTMLSpanElement> | ReactElement<HTMLSpanElement>[] | ReactNode;
  type?: 'answer' | 'heading' | 'body';
  debug?: boolean;
  triggerAnimation?: number;
}

export const Text = (props: TextProps) => {
  const { children, type = 'heading', debug = false, triggerAnimation = 0 } = props;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const childRefs = useRef<[HTMLSpanElement | null, boolean][]>([]);
  const [sizeChanged, setSizeChanged] = useState(Date.now());
  const [reformattedChildren, setReformattedChildren] = useState<(JSX.Element | string)[]>([]);
  const [isRendered, setRendered] = useState(false);
  const isLargeScreen = useMediaQuery('(min-width: 480px)');
  const shouldReformat = isRendered && (type === 'answer' || (type === 'heading' && isLargeScreen));

  useEffect(() => {
    setRendered(true);
  }, []);

  const parsedChildren = useMemo(() => {
    // just returning children before mount
    if (!isRendered) {
      return children;
    }

    // do simplified parse where we just want to swap spans for annotated types
    if (!shouldReformat) {
      return Children.map(children, (child) => {
        if (typeof child === 'string') {
          return child;
        } else {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (!child.props || typeof child.props.children !== 'string') {
            throw new Error('Passed children are invalid');
          }

          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const string = child.props.children;
          return <AnnotatedType>{string}</AnnotatedType>;
        }
      });
    }

    // take the passed children and and return a combination of <span> and <WideType> components for each word
    childRefs.current = [];
    const parsedChildren: JSX.Element[] = [];
    let index = 0;
    Children.forEach(children, (child) => {
      if (typeof child === 'string') {
        const parsedString = parseString(child, childRefs.current, index);
        index += parsedString.length;
        parsedChildren.push(...parsedString);
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (!child.props || typeof child.props.children !== 'string') {
          throw new Error('Passed children are invalid');
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const string = child.props.children;
        const parsedString = parseString(string, childRefs.current, index, true, type);
        index += string.split(' ').length;
        parsedChildren.push(...parsedString);
      }
    });
    return parsedChildren;
  }, [children, type, isRendered, shouldReformat]);

  // trigger reformatting when bounding box changes
  useEffect(() => {
    // no need for observer if we don't need to reformat
    if (!shouldReformat) {
      return;
    }

    // pleasing typescript
    if (rootRef.current === null) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      setSizeChanged(Date.now());
    });

    resizeObserver.observe(rootRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isRendered, shouldReformat]);

  // takes the parsed text, checks where line breaks occur and reformats it with added line breaks
  useEffect(() => {
    if (!shouldReformat) {
      return;
    }

    const reformattedChildren: (JSX.Element | string)[] = [];

    // description of steps:
    // - {n} consecutive normal words on multiple lines with added line breaks until we either:
    //    - find a wide word
    //    - are at the end
    // - {n} consecutive wide words on current line until we either:
    //    - find a normal word
    //    - find a line break
    //    - are at the end

    let isNormalSequence = false;
    let isWideSequence = false;
    let currentSequence = '';
    let wideSequenceIndex = 0;
    let wideSequenceStartedAfterNewLine = false;

    let previousTop: number | null = null;

    const startNormalSequence = () => {
      isNormalSequence = true;
      currentSequence = '';
    };

    const stopNormalSequence = (appendNewLine = false) => {
      isNormalSequence = false;
      reformattedChildren.push(<span>{currentSequence}</span>);

      if (appendNewLine) {
        reformattedChildren.push('\n');
      }
    };

    const appendNormalSequence = (prependNewLine: boolean, text: string) => {
      if (prependNewLine) {
        currentSequence += '\n';
      }
      currentSequence += text;
    };

    const startWideSequence = (afterNewLine = false) => {
      wideSequenceStartedAfterNewLine = afterNewLine;
      isWideSequence = true;
      currentSequence = '';
    };

    const appendWideSequence = (text: string) => {
      currentSequence += ' ' + text;
    };

    const stopWideSequence = (appendNewLine = false) => {
      isWideSequence = false;

      const component =
        type === 'heading' ? (
          <AnimatedHeadingType
            index={wideSequenceIndex}
            afterNewLine={wideSequenceStartedAfterNewLine}
            beforeNewLine={appendNewLine}
          >
            {currentSequence}
          </AnimatedHeadingType>
        ) : (
          <AnimatedAnswerType index={wideSequenceIndex}>{currentSequence}</AnimatedAnswerType>
        );

      reformattedChildren.push(component);
      wideSequenceIndex += 1;

      if (appendNewLine) {
        reformattedChildren.push('\n');
      }
    };

    for (let i = 0; i < childRefs.current.length; i++) {
      const [el, wide] = childRefs.current[i];

      if (el) {
        const top = el.offsetTop;
        const isNewLine = previousTop === null ? false : Math.abs(previousTop - top) > 10; // the 10 is currently arbitrarily picked
        previousTop = top;

        // when we get to a wide word
        if (wide) {
          if (isNormalSequence) {
            stopNormalSequence(isNewLine);
            startWideSequence();
            appendWideSequence(el.textContent || '');
          } else if (isWideSequence) {
            if (isNewLine) {
              stopWideSequence(true);
              startWideSequence(true);
            }
            appendWideSequence(el.textContent || '');
          } else {
            startWideSequence();
            appendWideSequence(el.textContent || '');
          }
        }
        // when we get to a normal word
        else {
          if (isNormalSequence) {
            appendNormalSequence(isNewLine, el.textContent || '');
          } else {
            if (isWideSequence) {
              stopWideSequence();
            }
            startNormalSequence();
            appendNormalSequence(isNewLine, el.textContent || '');
          }
        }
      }
    }

    // wrap up the current sequence, wether it's normal or wide
    if (isNormalSequence) {
      stopNormalSequence();
    } else {
      stopWideSequence();
    }

    setReformattedChildren(reformattedChildren);
  }, [sizeChanged, type, parsedChildren, shouldReformat]);

  return (
    <Root
      ref={rootRef}
      css={{
        whiteSpace: isLargeScreen ? 'pre-wrap' : 'normal',
      }}
    >
      <OriginalText
        css={{
          visibility: isRendered && shouldReformat && !debug ? 'hidden' : 'visible',
        }}
      >
        {parsedChildren}
      </OriginalText>
      {shouldReformat && (
        <ReformattedText
          css={{
            color: debug ? 'yellow' : 'inherit',
            opacity: debug ? 0.5 : 1,
            backgroundColor: debug ? 'red' : 'inherit',
          }}
        >
          {type === 'heading'
            ? reformattedChildren
            : Children.map(reformattedChildren, (child, index) => {
                if (typeof child === 'string') {
                  return <br />;
                }

                // for text of type 'answer' we need to clone otherwise animations won't retrigger
                return cloneElement(child, {
                  index,
                  position: index / (reformattedChildren.length - 1),
                  triggerAnimation,
                });
              })}
        </ReformattedText>
      )}
    </Root>
  );
};

const Root = styled('span', {
  display: 'inline-block',
  position: 'relative',
});

const OriginalText = styled('span', {});

const ReformattedText = styled('span', {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
});
